import {
  DEFAULT_CURRENCY_LOCALES,
  DEFAULT_CURRENCY_RATES,
  DEFAULT_SERVICE_BASE_PRICES_CHF,
  DEFAULT_SITE_CURRENCY,
  RATE_ENV_KEYS,
  SERVICE_PRICE_ENV_KEYS,
  SUPPORTED_CURRENCIES,
  type CurrencyCode,
  type ServicePriceKey,
} from '../config/pricing';

export type PricingEnv = Readonly<Record<string, string | undefined>>;

export type CurrencyRates = Readonly<Record<CurrencyCode, number>>;

export interface ServicePriceSnapshot {
  serviceKey: ServicePriceKey;
  basePriceChf: number;
  convertedPrice: number;
  currency: CurrencyCode;
  formattedPrice: string;
  hasConfiguredPrice: boolean;
}

const isCurrencyCode = (value: string): value is CurrencyCode =>
  (SUPPORTED_CURRENCIES as readonly string[]).includes(value);

const parsePositiveNumber = (value: string | undefined, fallback: number) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : fallback;
};

const parseNonNegativeNumber = (
  value: string | undefined,
  fallback: number,
) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? parsedValue
    : fallback;
};

const resolveSupportedLocale = (
  locale: string | undefined,
  currency: CurrencyCode,
) => {
  if (!locale) {
    return DEFAULT_CURRENCY_LOCALES[currency];
  }

  const [supportedLocale] = Intl.NumberFormat.supportedLocalesOf([locale]);

  return supportedLocale ?? DEFAULT_CURRENCY_LOCALES[currency];
};

export const getSiteCurrency = (
  env: PricingEnv = process.env,
): CurrencyCode => {
  const configuredCurrency = env.REACT_APP_SITE_CURRENCY?.toUpperCase();

  return configuredCurrency && isCurrencyCode(configuredCurrency)
    ? configuredCurrency
    : DEFAULT_SITE_CURRENCY;
};

export const getCurrencyRates = (
  env: PricingEnv = process.env,
): CurrencyRates => ({
  CHF: parsePositiveNumber(env[RATE_ENV_KEYS.CHF], DEFAULT_CURRENCY_RATES.CHF),
  USD: parsePositiveNumber(env[RATE_ENV_KEYS.USD], DEFAULT_CURRENCY_RATES.USD),
  EUR: parsePositiveNumber(env[RATE_ENV_KEYS.EUR], DEFAULT_CURRENCY_RATES.EUR),
});

export const getServiceBasePriceChf = (
  serviceKey: ServicePriceKey,
  env: PricingEnv = process.env,
) =>
  parseNonNegativeNumber(
    env[SERVICE_PRICE_ENV_KEYS[serviceKey]],
    DEFAULT_SERVICE_BASE_PRICES_CHF[serviceKey],
  );

export const convertChfToCurrency = (
  amountChf: number,
  targetCurrency: CurrencyCode,
  rates: CurrencyRates,
) =>
  // Rates are normalized against CHF, so the target amount preserves the
  // CHF reference before dividing by the target currency rate.
  amountChf * (rates.CHF / rates[targetCurrency]);

export const formatCurrencyValue = (
  amount: number,
  currency: CurrencyCode,
  locale?: string,
) =>
  new Intl.NumberFormat(resolveSupportedLocale(locale, currency), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

export const getServicePriceSnapshot = (
  serviceKey: ServicePriceKey,
  options?: {
    currency?: CurrencyCode;
    env?: PricingEnv;
    locale?: string;
    rates?: CurrencyRates;
  },
): ServicePriceSnapshot => {
  const env = options?.env ?? process.env;
  const currency = options?.currency ?? getSiteCurrency(env);
  const rates = options?.rates ?? getCurrencyRates(env);
  const basePriceChf = getServiceBasePriceChf(serviceKey, env);
  const convertedPrice = convertChfToCurrency(basePriceChf, currency, rates);

  return {
    serviceKey,
    basePriceChf,
    convertedPrice,
    currency,
    formattedPrice: formatCurrencyValue(
      convertedPrice,
      currency,
      options?.locale,
    ),
    hasConfiguredPrice: basePriceChf > 0,
  };
};
